/**
 * Facebook像素管理工具类
 * 用于动态初始化和跟踪Facebook像素事件
 */
class FacebookPixel {
  constructor() {
    this.pixelId = null
    this.isInitialized = false
  }

  /**
   * 初始化Facebook像素
   * @param {string} pixelId - Facebook像素ID
   */
  init(pixelId) {
    // 验证像素ID
    if (!pixelId || pixelId === 'null' || pixelId === 'undefined' || pixelId.trim() === '') {
      console.warn('Facebook像素ID无效:', pixelId)
      return
    }
    
    // 检查是否已经用相同的像素ID初始化过
    if (this.isInitialized) {
      console.log('Facebook像素已初始化，跳过重复初始化')
      return
    }
    
    this.pixelId = pixelId

    // 如果已经初始化过，先清理
    if (this.isInitialized) {
      this.destroy()
    }

    // 动态加载Facebook像素代码
    this.loadFacebookPixel()
    this.isInitialized = true
  }

  /**
   * 动态加载Facebook像素代码
   */
  loadFacebookPixel() {
    // 创建Facebook像素脚本 - 使用最新版本
    if(this.pixelId){
      const script = document.createElement('script')
      script.innerHTML = `
        !function(f,b,e,v,n,t,s)
        {if(f.fbq)return;n=f.fbq=function(){n.callMethod?
        n.callMethod.apply(n,arguments):n.queue.push(arguments)};
        if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';
        n.queue=[];t=b.createElement(e);t.async=!0;
        t.src=v;s=b.getElementsByTagName(e)[0];
        s.parentNode.insertBefore(t,s)}(window, document,'script',
        'https://connect.facebook.net/en_US/fbevents.js');
        fbq('init', '${this.pixelId}', {}, {
          agent: 'plwo'
        });
        fbq('track', 'PageView');
      `
      document.head.appendChild(script)

      // 创建noscript标签 - 使用最新格式
      const noscript = document.createElement('noscript')
      noscript.innerHTML = `
        <img height="1" width="1" style="display:none"
        src="https://www.facebook.com/tr?id=${this.pixelId}&ev=PageView&noscript=1"
        />
      `
      document.head.appendChild(noscript)
    }
  }

  /**
   * 跟踪页面浏览事件
   */
  trackPageView() {
    if (this.isInitialized && window.fbq) {
      window.fbq('track', 'PageView')
    }
  }

  /**
   * 跟踪转化事件
   * @param {string} eventName - 事件名称
   * @param {object} parameters - 事件参数
   */
  trackEvent(eventName, parameters = {}) {
    if (this.isInitialized && window.fbq) {
      window.fbq('track', eventName, parameters)
      console.log(`Facebook像素事件已发送: ${eventName}`, parameters)
    } else {
      console.warn('Facebook像素未初始化，无法发送事件')
    }
  }

  /**
   * 跟踪自定义事件
   * @param {string} eventName - 事件名称
   * @param {object} parameters - 事件参数
   */
  trackCustomEvent(eventName, parameters = {}) {
    if (this.isInitialized && window.fbq) {
      window.fbq('trackCustom', eventName, parameters)
      console.log(`Facebook像素自定义事件已发送: ${eventName}`, parameters)
    } else {
      console.warn('Facebook像素未初始化，无法发送自定义事件')
    }
  }

  /**
   * 跟踪购买事件
   * @param {object} purchaseData - 购买数据
   */
  trackPurchase(purchaseData) {
    this.trackEvent('Purchase', {
      value: purchaseData.value || 0,
      currency: purchaseData.currency || 'USD',
      content_ids: purchaseData.content_ids || [],
      content_type: purchaseData.content_type || 'product'
    })
  }

  /**
   * 跟踪注册事件
   */
  trackRegistration() {
    this.trackEvent('CompleteRegistration')
  }

  /**
   * 跟踪登录事件
   */
  trackLogin() {
    this.trackEvent('Login')
  }

  /**
   * 跟踪添加购物车事件
   * @param {object} cartData - 购物车数据
   */
  trackAddToCart(cartData) {
    this.trackEvent('AddToCart', {
      value: cartData.value || 0,
      currency: cartData.currency || 'USD',
      content_ids: cartData.content_ids || [],
      content_type: cartData.content_type || 'product'
    })
  }

  /**
   * 跟踪开始结账事件
   * @param {object} checkoutData - 结账数据
   */
  trackInitiateCheckout(checkoutData) {
    this.trackEvent('InitiateCheckout', {
      value: checkoutData.value || 0,
      currency: checkoutData.currency || 'USD',
      content_ids: checkoutData.content_ids || [],
      content_type: checkoutData.content_type || 'product'
    })
  }

  /**
   * 销毁Facebook像素
   */
  destroy() {
    // 移除Facebook像素脚本
    const scripts = document.querySelectorAll('script')
    scripts.forEach(script => {
      if (script.innerHTML.includes('fbevents.js')) {
        script.remove()
      }
    })

    // 移除noscript标签
    const noscripts = document.querySelectorAll('noscript')
    noscripts.forEach(noscript => {
      if (noscript.innerHTML.includes('facebook.com/tr')) {
        noscript.remove()
      }
    })

    // 清理全局变量
    if (window.fbq) {
      delete window.fbq
    }
    if (window._fbq) {
      delete window._fbq
    }
    
    this.isInitialized = false
    this.pixelId = null
  }

  /**
   * 获取当前像素ID
   */
  getPixelId() {
    return this.pixelId
  }

  /**
   * 检查是否已初始化
   */
  isReady() {
    return this.isInitialized && window.fbq
  }

  /**
   * 启用自动高级匹配
   */
  enableAutoAdvancedMatching() {
    if (this.isInitialized && window.fbq) {
      window.fbq('consent', 'grant')
      console.log('Facebook像素自动高级匹配已启用')
    }
  }

  /**
   * 禁用自动高级匹配
   */
  disableAutoAdvancedMatching() {
    if (this.isInitialized && window.fbq) {
      window.fbq('consent', 'revoke')
      console.log('Facebook像素自动高级匹配已禁用')
    }
  }

  /**
   * 发送增强事件（Enhanced Events）
   * @param {string} eventName - 事件名称
   * @param {object} parameters - 事件参数
   */
  trackEnhancedEvent(eventName, parameters = {}) {
    if (this.isInitialized && window.fbq) {
      // 添加增强事件标识
      const enhancedParams = {
        ...parameters,
        _eventName: eventName,
        _eventTime: Math.floor(Date.now() / 1000)
      }
      window.fbq('track', eventName, enhancedParams)
      console.log(`Facebook像素增强事件已发送: ${eventName}`, enhancedParams)
    } else {
      console.warn('Facebook像素未初始化，无法发送增强事件')
    }
  }

  /**
   * 设置用户数据（用于高级匹配）
   * @param {object} userData - 用户数据
   */
  setUserData(userData) {
    if (this.isInitialized && window.fbq) {
      window.fbq('setUserData', userData)
      console.log('Facebook像素用户数据已设置:', userData)
    } else {
      console.warn('Facebook像素未初始化，无法设置用户数据')
    }
  }
}

// 创建单例实例
const facebookPixel = new FacebookPixel()

export default facebookPixel


